import { PlusCircleOutlined } from "@ant-design/icons";
import { Box, Modal, Snackbar, TextField } from '@mui/material';
import { AddCircle, CloseOutlined, Delete } from '@mui/icons-material';
import { Alert } from '@mui/material';
import Loading from "components/Loading";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useCurrentClass } from "store/classes/selectors";
import {
  handleCreateRubric,
  handleEditRubric,
  handleGetRubric,
} from "store/rubric/thunks";
import { useCurrentUser } from "store/users/selectors";
import styled from "styled-components";
import * as Sentry from '@sentry/react';
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import SentryWrap from 'components/Sentry';

const criterionPayload = {
  title: "",
  rubrics_meta: [
    {
      criterionTitle: "", // required
      criterionDescription: "",
      points: [
        {
          pointText: "", // numeric- required
          title: "",
          description: "",
        },
      ],
    },
  ],
  maxPoint: 0, //numeric
};

const CreateRubricModal = ({
  open,
  close,
  editRubric = null,
  setEditRubric = () => {},
  is_placement
}) => {
  const showAlert = useAlert();
  const innerRef = useRef(null);
  const [openAlert, setAlert] = useState({ open: false, message: "" });
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mainTitle, setMainTitle] = useState("");
  const [criterion, setCriterion] = useState(
    criterionPayload?.rubrics_meta || []
  );
  const containerRefs = useRef({});

  const is_clone_rubric = useMemo(() => {
    return editRubric?.user_id !== currentUser?.id
  }, [editRubric, currentUser]);

  const is_my_rubric = useMemo(() => {
    return editRubric?.user_id === currentUser?.id
  }, [editRubric, currentUser]);

  const editDisabled = useMemo(() => {
    return is_my_rubric ? editRubric && Number(editRubric?.total_assigned) > 0 :
    is_clone_rubric ? false : true
  }, [is_my_rubric, is_clone_rubric, editRubric]);


  const onAutoScroll = () => {
    if (innerRef.current) {
      setTimeout(() => {
        innerRef.current.scrollTo(0, innerRef.current.scrollHeight)
      }, 500)
    }
  }

  const overallMaxPoint = useMemo(() => {
    if (criterion) {
      return criterion?.reduce((totalMax, criterionItem) => {
        const criterionMax = Math.max(
          ...criterionItem.points.map(
            (point) => parseFloat(point.pointText) || 0
          )
        );
        return totalMax + criterionMax || 0;
      }, 0);
    }
  }, [criterion, editRubric]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("body-overflow-hidden");
    }

    return () => {
      document.body.classList.remove("body-overflow-hidden");
    };
  }, [open]);

  useEffect(() => {
    if (editRubric && Object.entries(editRubric)?.length !== 0) {
      setCriterion(editRubric?.rubrics_meta);
      setMainTitle(editRubric?.title);
    } else {
      setCriterion(criterionPayload?.rubrics_meta);
      setMainTitle("");
    }
  }, [editRubric]);

  const AddCriterion = () => {
    const data = {
      criterionTitle: "",
      criterionDescription: "",
      points: [
        {
          points: "",
          title: "",
          description: "",
        },
      ],
    };
    setCriterion([...criterion, data]);
    onAutoScroll()
  };

  const AddPoint = (selectedIndex, pointIndex) => {
    const data = {
      pointText: "",
      title: "",
      description: "",
    };
    setCriterion((prev) => {
      return prev.map((element, index) => {
        if (index === selectedIndex) {
          const pointsData = [...element.points];
          const nextIndex = pointIndex + 1;
          pointsData.splice(nextIndex, 0, data);
           shiftErrors(selectedIndex, pointIndex, 'after');
          return {
            ...element,
            points: pointsData,
          };
        }
        return element;
      });
    });
  };

  const scrollToPoint = (criterionIndex, pointIndex) => {
    setTimeout(() => {
      const container = containerRefs.current[criterionIndex];
      if (!container?.children?.length) return;
      const child = container.children[pointIndex];
      const scrollLeft = child ? child.offsetLeft : (container.children[0]?.offsetWidth ?? 0) * pointIndex;
      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }, 0);
  };
  
  const deletePoint = (selectedIndex, pointIndex) => {
    if (!criterion[selectedIndex] || criterion[selectedIndex].points.length === 1) {
      return;
    }
    setCriterion((prev) =>
      prev.map((element, index) => {
        if (selectedIndex === index) {
          const updatedPoints = element.points.filter((_, idx) => idx !== pointIndex);
           shiftErrors(selectedIndex, pointIndex, 'delete');
          return {
            ...element,
            points: updatedPoints,
          };
        }
        return element;
      })
    );
  };

  const shiftErrors = (selectedIndex, pointIndex, action) => {
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      Object.keys(prevErrors).forEach((key) => {
        const match = key.match(new RegExp(`criterion_${selectedIndex}_point_(\\d+)`));
        if (match) {
          const index = parseInt(match[1], 10);
          if (action === 'after' && index >= pointIndex + 1) {
            newErrors[`criterion_${selectedIndex}_point_${index + 1}`] = prevErrors[key];
            delete newErrors[key];
          }
          if (action === 'before' && index >= pointIndex) {
            newErrors[`criterion_${selectedIndex}_point_${index + 1}`] = prevErrors[key];
            delete newErrors[key];
          }
          if (action === 'delete' && index > pointIndex) {
            newErrors[`criterion_${selectedIndex}_point_${index - 1}`] = prevErrors[key];
            delete newErrors[key];
          }
        }
      });
  
      return newErrors;
    });
  };
  
  
  const AddPointBefore = (selectedIndex, pointIndex) => {
    const data = {
      pointText: "",
      title: "",
      description: "",
    };
    setCriterion((prev) =>
      prev.map((element, index) => {
        if (index === selectedIndex) {
          const pointsData = [...element.points];
          pointsData.splice(pointIndex, 0, data);
          shiftErrors(selectedIndex, pointIndex, 'before');
          return {
            ...element,
            points: pointsData,
          };
        }
        return element;
      })
    );
  };
  

  const deleteCriterion = (selectedIndex) => {
    if (criterion.length === 1) {
      return;
    }
    setCriterion((prev) => prev.filter((_, index) => index !== selectedIndex));
  };

  const updateData = (
    indexValue,
    updateValue = "criterion",
    key,
    value,
    pointIndex
  ) => {
    setCriterion((prevCriterion) => {
      return prevCriterion.map((criterionItem, index) => {
        if (index === indexValue) {
          if (updateValue === "criterion") {
            handleInputChange(`criterion_${indexValue}_title`, value);
            return { ...criterionItem, [key]: value };
          } else if (updateValue === "pointIndex") {
            handleInputChange(
              `criterion_${indexValue}_point_${pointIndex}`,
              value
            );
            const updatedPoints = criterionItem.points.map((point, idx) => {
              if (idx === pointIndex) {
                return { ...point, [key]: value };
              }
              return point;
            });
            return { ...criterionItem, points: updatedPoints };
          }
        }
        return criterionItem;
      });
    });
  };



  const saveHandler = async () => {
    setIsFormSubmitted(true);
    if (!validateForm()) {
      showAlert("Please review the form. Some fields are missing, incomplete, or duplicated.", "error");
      return;
    }
    try {
      let rubricResponse = {};
      let payload = {};
      setIsLoading(true);

      if (is_clone_rubric && !is_placement) {
        payload = {
          user_id: currentUser?.id,
          created_by: currentUser?.role,
          book_id: "",
          page: "",
          clone_id: editRubric?.id,
          title: mainTitle,
          rubrics_meta: criterion,
          max_point: overallMaxPoint,
          is_placement:is_placement,
          is_hide: false
        };
        rubricResponse =  await dispatch(handleCreateRubric(payload));
      }
      else if (editRubric && Object.entries(editRubric)?.length !== 0) {
        payload = {
          ...editRubric,
          id: editRubric?.id,
          title: mainTitle,
          rubrics_meta: criterion,
          max_point: overallMaxPoint,
          is_placement:is_placement
        };
        await dispatch(handleEditRubric(payload));
      } else {
        payload = {
          user_id: currentUser?.id,
          created_by: currentUser?.role,
          book_id: "",
          page: "",
          title: mainTitle,
          rubrics_meta: criterion,
          max_point: overallMaxPoint,
          class_id: currentClass?.id || "",
          is_hide: false,
          is_placement:is_placement
        };
        rubricResponse =  await dispatch(handleCreateRubric(payload));
      }
      const getRubricData =  await dispatch(handleGetRubric(currentUser?.id));

      setCriterion(criterionPayload?.rubrics_meta);
      setMainTitle("");
      setEditRubric(null);
      close(rubricResponse,getRubricData, is_clone_rubric);
    } catch (error) {
      Sentry.captureException(`371 Error during createRubricModal: ${error}`);
      console.log(error);
    } finally {
      setIsLoading(false);
      setIsFormSubmitted(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    if (isFormSubmitted) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;


    if (!mainTitle.trim() || !mainTitle || mainTitle === "" || mainTitle?.length === 0) {
      newErrors[`main_title`] = `Title is required Field`;
      isValid = false;
    }
    criterion.forEach((crit, criterionIndex) => {
      if (!crit.criterionTitle.trim()) {
        newErrors[`criterion_${criterionIndex}_title`] =
          "Criterion title is required";
        isValid = false;
      }

      const pointTextMap = new Map();
      crit.points.forEach((point, pointIndex) => {
        const trimmedPointText = point?.pointText?.trim().replace(/^0+(?!$)/, "");

        if (!trimmedPointText) {
          newErrors[`criterion_${criterionIndex}_point_${pointIndex}`] =
            "Point value is required";
          isValid = false;
        } else {
          if (pointTextMap.has(trimmedPointText)) {
            pointTextMap.get(trimmedPointText).push(pointIndex);
          } else {
            pointTextMap.set(trimmedPointText, [pointIndex]);
          }
        }
      });

      pointTextMap.forEach((indices) => {
        if (indices.length > 1) {
          indices.forEach((index) => {
            newErrors[`criterion_${criterionIndex}_point_${index}`] =
              "Point must be unique within the criterion";
          });
          isValid = false;
        }
      });
    });

    setErrors(newErrors);
    return isValid;
  };

  const closeHandler = () => {
    close();
    setCriterion(criterionPayload?.rubrics_meta);
    setMainTitle("");
    setEditRubric(null);
    setErrors([])
  };

  const toastHandler = (message) => {
    setAlert({
      open: true,
      message: message,
    });
  };
  const closeDueDateAlert = () => {
    setAlert({ open: false, message: "" });
  };

  return (
    <SentryWrap>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={openAlert.open}
        autoHideDuration={3000}
        onClose={closeDueDateAlert}
      >
        <Alert onClose={closeDueDateAlert} severity="error">
          <strong>ALERT:</strong> {openAlert.message}
        </Alert>
      </Snackbar>
      <Modal open={open} onClose={close}>
        <ModalContent orientation={window.screen.orientation}>
          <HeaderContainer className="flex items-center">
            <CloseOutlined onClick={closeHandler} className="cursor-pointer" />
            <RubricLabel className="">Rubric</RubricLabel>
            <SaveButton
              onClick={saveHandler}
              className="cursor-pointer"
              variant="contained"
              color="success"
              disabled={isLoading}
              style={{
                opacity: isLoading ? "0.7" : "1",
                cursor: isLoading ? "default" : "pointer",
              }}
            >
              {isLoading ? <Loading white={true} size={20} /> : "Save"}
            </SaveButton>
          </HeaderContainer>

          <div className="p-16px-32px max-w-1390px flex-col flex box-sizing-border-box self-center w-full" style={{overflow:"scroll",marginBottom:50 }}>
            <AssignmentName>
              <CustomTextField
                id="title"
                name="title"
                label="title"
                variant="standard"
                value={mainTitle}
                disabled={
                  editDisabled
                }
                required={true}
                onChange={(e) => {
                  handleInputChange(`main_title`, e.target.value);
                  setMainTitle(e.target.value);
                }}
                error={!!errors[`main_title`]}
                helperText={errors[`main_title`]}
              />
            </AssignmentName>
            <RubricDescription>
              Add the criteria you’ll use to evaluate student work as well as
              any performance levels or descriptions you want to include.
              Students will receive a copy of this rubric with their assignment.
            </RubricDescription>

            <div className="text-end rubricData-title">
              Max Points:-{" "}
              <span className="text-blue-10">{overallMaxPoint}</span>
            </div>

            <CreitionMainContainer ref={innerRef}>
              {criterion?.map((data, criterionIndex) => {
                const deleteButtonCategoryDisabled =
                  criterion?.length === 1 ||
                  editDisabled;
                const deletePointsButton =
                  data?.points?.length === 1 ||
                  editDisabled;
                return (
                  <>
                    

                    <CreitionContainer className="createRubricUpr">
                      <div className="mt-10px w-full flex items-center justify-between">
                        <CustomTextField
                          id="criterion-title"
                          name="criterion-title"
                          label="Criterion title"
                          variant="outlined"
                          required={true}
                          value={data?.criterionTitle}
                          error={!!errors[`criterion_${criterionIndex}_title`]}
                          helperText={
                            errors[`criterion_${criterionIndex}_title`]
                          }
                          onChange={(e) =>
                            updateData(
                              criterionIndex,
                              "criterion",
                              "criterionTitle",
                              e.target.value
                            )
                          }
                          size="small"
                        />
                        <StyledButton
                          className="text-red-10 float-right mb-20px cursor-pointer mr-15px"
                          disabled={deleteButtonCategoryDisabled}
                        >
                          <Delete
                            style={{
                              cursor: deleteButtonCategoryDisabled && "default",
                              opacity: deleteButtonCategoryDisabled && "0.4",
                            }}
                            onClick={() => deleteCriterion(criterionIndex)}
                            className=""
                          />
                        </StyledButton>
                      </div>

                      <div className="mt-20px w-full">
                        <CustomTextField
                          id="criterion-description"
                          name="criterion-description"
                          variant="outlined"
                          label="Criterion Description"
                          required={false}
                          value={data?.criterionDescription}
                          onChange={(e) =>
                            updateData(
                              criterionIndex,
                              "criterion",
                              "criterionDescription",
                              e.target.value
                            )
                          }
                          multiline={true}
                          size="small"
                        />
                      </div>

                      {/* // point one drawer */}
                      <PointContainer ref={(el) => { if (el) containerRefs.current[criterionIndex] = el; }}>
                        {data?.points?.map((element, pointIndex) => {
                          return (
                            <div style={{paddingRight: pointIndex == data?.points?.length - 1 ? '24px' : '0px', display: 'flex', flex: '0 0 auto'}} >
                          <PointDrawer  key={pointIndex}>
                            {pointIndex === 0 &&
                              !(
                                editRubric &&
                                Number(editRubric?.total_assigned) > 0
                              ) || (is_clone_rubric) && (
<PlusPointDrawer
                                  onClick={() => {
                                    AddPointBefore(criterionIndex, pointIndex);
                                    scrollToPoint(criterionIndex, pointIndex-1);
                                  }}
                                >
                                  <PlusCircleOutlined className="text-blue-10 text-ft24px cursor-pointer" />
                                </PlusPointDrawer>
                              )}

                            <InputPointContainer>
                              <StyledButton
                                disabled={deletePointsButton}
                                className="text-red-10 float-right mb-20px cursor-pointer"
                              >
                                <Delete
                                  style={{
                                    opacity: deletePointsButton && "0.4",
                                    cursor: deletePointsButton && "default",
                                  }}
                                  onClick={() =>
                                    deletePoint(criterionIndex, pointIndex)
                                  }
                                />
                              </StyledButton>

                              <div className="mt-20px w-full">
                                <CustomTextField
                                  id="point"
                                  name="point"
                                  variant="outlined"
                                  label="Points(Required)"
                                  required={true}
                                  type="number"
                                  disabled={
                                    editDisabled
                                  }
                                  value={element?.pointText}
                                  onChange={(e) =>
                                    updateData(
                                      criterionIndex,
                                      "pointIndex",
                                      "pointText",
                                      e.target.value,
                                      pointIndex
                                    )
                                  }
                                  error={
                                    !!errors[
                                      `criterion_${criterionIndex}_point_${pointIndex}`
                                    ]
                                  }
                                  helperText={
                                    errors[
                                      `criterion_${criterionIndex}_point_${pointIndex}`
                                    ]
                                  }
                                  multiline={true}
                                  size="small"
                                />
                              </div>

                              <div className="mt-20px w-full">
                                <CustomTextField
                                  id="point"
                                  name="point"
                                  variant="outlined"
                                  label="Level Title"
                                  required={false}
                                  value={element?.title}
                                  onChange={(e) =>
                                    updateData(
                                      criterionIndex,
                                      "pointIndex",
                                      "title",
                                      e.target.value,
                                      pointIndex
                                    )
                                  }
                                  multiline={true}
                                  size="small"
                                />
                              </div>

                              <div className="mt-20px w-full">
                                <CustomTextField
                                  id="point"
                                  name="point"
                                  variant="outlined"
                                  label="Description"
                                  required={false}
                                  value={element?.description}
                                  onChange={(e) =>
                                    updateData(
                                      criterionIndex,
                                      "pointIndex",
                                      "description",
                                      e.target.value,
                                      pointIndex
                                    )
                                  }
                                  multiline={true}
                                  size="small"
                                />
                              </div>
                            </InputPointContainer>
                            {!(
                              editRubric &&
                              Number(editRubric?.total_assigned) > 0
                            ) && (
                              <PlusPointDrawer
                                onClick={() => {
                                    AddPoint(criterionIndex, pointIndex);
                                    scrollToPoint(criterionIndex, pointIndex);
                                  }}
                              >
                                <PlusCircleOutlined className="text-blue-10 text-ft24px cursor-pointer" />
                              </PlusPointDrawer>
                            )}
                          </PointDrawer>
                          </div>
                        )}
              )}
                      </PointContainer>
                    </CreitionContainer>
                    {criterion?.length > 0 &&
                      criterion?.length - 1 !== criterionIndex && (
                        <hr color="lightGrey" />
                      )}
                  </>
                );
              })}
            </CreitionMainContainer>
            <div className="w-full mt-10px">
              <AddCreitionButton
                onClick={AddCriterion}
                className="flex items-center"
                disabled={
                  editDisabled
                }
              >
                <AddCircle className="mr-8px" />
                Add a criterion
              </AddCreitionButton>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </SentryWrap>
  );
};

const CustomTextField = ({
  id,
  name,
  label,
  required = false,
  onChange,
  type = "text",
  variant = "outlined",
  value,
  error,
  ...props
}) => {
  const handleChange = (e) => {
    if (type === "number") {
      const numValue = e.target.value.replace(/[^0-9]/g, "");
      onChange({ ...e, target: { ...e.target, value: numValue } });
    } else {
      onChange(e);
    }
  };

  return (
    <Box
      component="form"
      
      noValidate
      autoComplete="off"
    >
      <TextField
        id={id}
        name={name}
        label={label}
        required={required}
        type={type}
        variant={variant}
        onChange={handleChange}
        value={value}
        error={error}
        helperText={error && required ? "This field is required" : ""}
        multiline
        {...props}
        slotProps={{
          textarea: {
            sx: {
              padding: "2px 8px",
              lineHeight: "16px",
              height: "16px",
              fontSize: "14px",
              resize: "none",
              overflow: "hidden", // optional: hide scrollbar initially
            },
          },
          label: {
            shrink: true,
            required: required,
          },
        }}
      />
    </Box>
  );
};

const StyledButton = styled.button`
  background: none;
  border: none;
  outline: none;
  padding: none;
`;

const PlusPointDrawer = styled.div`
  align-self: stretch;
  justify-content: center;
  position: relative;
  width: 0;
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
`;

const InputPointContainer = styled.div`
  flex: 1 0 auto;
  padding: 16px;
  width: 234px;
`;

const PointDrawer = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  overflow-x: visible;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  margin-top: 20px;
`;

const PointContainer = styled.div`
  display: flex;
  margin: 8px 0px;
  padding: 0px 24px 8px 24px;
  overflow-x: auto;
  max-width: 100%;
  width: 100%;
  background: linear-gradient(90deg, #f8f9fa 0%, #ffffff 20%, #ffffff 80%, #f8f9fa 100%);
  border-radius: 4px;
`;

const AddCreitionButton = styled.button`
  border: 1px solid #dadce0;
  box-sizing: border-box;
  color: #1a73e8;
  height: 36px;
  line-height: 34px;
  padding: 10px 15px;
  background: transparent;
  cursor: pointer;
  border-rdaius: 14px;

  &:hover {
    border-color: #d2e3fc;
  }
  &:disabled {
    color: #b0b0b0;
    border-color: #e0e0e0;
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const CreitionMainContainer = styled.div`
  margin-top: 24px;
  max-height: calc(100vh - 340px);
  min-height: 200px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  scroll-behavior: smooth;
`;

const CreitionContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const AssignmentName = styled.p`
  color: rgb(32, 33, 36);
  font-family: "Google Sans", Roboto, Arial, sans-serif;
  line-height: 2.25rem;
  font-size: 1.75rem;
  letter-spacing: 0;
  font-weight: 400;
  max-width: 300px;
`;

const HeaderContainer = styled.div`
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  line-height: 1.5rem;
  padding-inline: 20px;
  padding-top: 15px;
  padding-bottom: 15px;
`;

const ModalContent = styled.div`
  background-color: white;
  position: relative;
  width: ${(props) => (props.orientation === "landscape" ? "90%" : "100%")};
  height: ${(props) => (props.orientation === "landscape" ? "80%" : "100%")};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const RubricLabel = styled.p`
  color: rgb(60, 64, 67);
  flex-grow: 1;
  flex-shrink: 1;
  margin: 16px;
  word-wrap: break-word;
  font-size: 20px;
  font-weight: bold;
`;

const SaveButton = styled.button`
  color: white;
  background-color: #1a73e8;
  width: 140px;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.25px;
  line-height: 36px;
  text-decoration: none;
  text-transform: none;
  text-transform: none;
  min-width: auto;
  outline: none;
  overflow: hidden;
  position: relative;
  text-align: center;
  border: 0;
  border-radius: 4px;
`;

const RubricDescription = styled.p`
  color: rgb(60, 64, 67);
  font-family: Roboto, Arial, sans-serif;
  line-height: 1.25rem;
  font-size: 0.875rem;
  letter-spacing: 0.0142857143em;
  font-weight: 400;
`;

export default CreateRubricModal;
